import { CardPaperType } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import { Box, ButtonV2, DialogV2, DrawerV2, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import React from 'react';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';

import { useTranslation } from '@shared/core';
import { usePaperTypes } from '@apps/card/hooks/usePaperTypes';

interface CardProps {
  type: CardPaperType;
}

// This needs to be migrated to be Product Config driven.
const CURRENTLY_OFFERED_PAPER_TYPES = [CardPaperType.signature, CardPaperType.semiGloss, CardPaperType.pearlescent, CardPaperType.doubleThick] as const;

const Card: React.FC<CardProps> = ({ type }) => {
  const { tq } = useTranslation('stationery');
  const paperTypes = usePaperTypes();
  const paperType = paperTypes[type];
  if (!paperType) return null;

  return (
    <Flex
      flexDirection="column"
      gap={5}
    >
      <Box
        as="img"
        src={paperType.imageUrl}
        objectFit="cover"
        objectPosition="center bottom"
        width="100%"
        height="124px"
        borderRadius={3}
        backgroundColor="mono3"
      />
      <Flex
        flexDirection="column"
        gap={3}
      >
        <TextV2 typographyVariant="button1">{paperType.label}</TextV2>
        <TextV2 typographyVariant="label2">{paperType.description}</TextV2>
        <Box
          as="ul"
          css={{ listStyle: 'disc' }}
          paddingLeft={pxToRem(15)}
          paddingTop={3}
        >
          <TextV2
            as="li"
            typographyVariant="label2"
          >
            {tq`Weight: ${paperType.weight}`}
          </TextV2>
          {!!paperType.thickness && (
            <TextV2
              as="li"
              typographyVariant="label2"
            >
              {tq`Thickness: ${paperType.thickness}`}
            </TextV2>
          )}
        </Box>
      </Flex>
    </Flex>
  );
};

interface PaperTypesDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

const PaperTypesDialog: React.FC<PaperTypesDialogProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation('stationery');
  const tPaperTypesDialog = t('cardCustomizer', 'paperTypesDialog');
  const isMobile = useIsMobileScreen();

  if (isMobile) {
    return (
      <DrawerV2
        isOpen={isOpen}
        onClose={onClose}
        anchor="bottom"
        size={0.95}
        stickyBehavior="header-footer"
        overrides={{
          Content: {
            props: {
              borderRadius: '0.5rem 0.5rem 0 0',
            },
          },
        }}
      >
        <DrawerV2.Header borderBottom="1px solid #EBEBEB">
          <Flex
            justifyContent="space-between"
            alignItems="center"
          >
            <TextV2 typographyVariant="hed3">{tPaperTypesDialog.title()}</TextV2>
            <DrawerV2.CloseButton />
          </Flex>
        </DrawerV2.Header>
        <DrawerV2.Body>
          <TextV2
            typographyVariant="body1"
            marginBottom={9}
          >
            {tPaperTypesDialog.description()}
          </TextV2>
          <Flex
            flexDirection="column"
            gap={7}
          >
            {CURRENTLY_OFFERED_PAPER_TYPES.map(type => (
              <Card
                key={type}
                type={type}
              />
            ))}
          </Flex>
        </DrawerV2.Body>
        <DrawerV2.Footer borderTop="1px solid #EBEBEB">
          <ButtonV2
            flex="1"
            intent="neutral"
            shape="rounded"
            onClick={onClose}
          >
            {tPaperTypesDialog.done()}
          </ButtonV2>
        </DrawerV2.Footer>
      </DrawerV2>
    );
  }

  return (
    <DialogV2
      isOpen={isOpen}
      onClose={onClose}
      size="3xl"
      overrides={{
        Body: {
          props: {
            paddingBottom: '4rem !important',
          },
        },
        CloseButton: {
          props: {
            top: pxToRem(28),
            right: pxToRem(12),
          },
        },
      }}
    >
      <DialogV2.Header
        paddingX={9}
        paddingY={8}
      >
        <TextV2
          typographyVariant="hed6"
          fontWeight={600}
          marginBottom={5}
        >
          {tPaperTypesDialog.title()}
        </TextV2>
        <TextV2 typographyVariant="body2">{tPaperTypesDialog.description()}</TextV2>
        <DialogV2.CloseButton />
      </DialogV2.Header>
      <DialogV2.Body paddingX={9}>
        <Box
          width="100%"
          display="grid"
          gridTemplateColumns="1fr 1fr"
          gap={6}
        >
          {CURRENTLY_OFFERED_PAPER_TYPES.map(type => (
            <Flex
              key={type}
              padding={5}
              paddingBottom={6}
            >
              <Card type={type} />
            </Flex>
          ))}
        </Box>
      </DialogV2.Body>
    </DialogV2>
  );
};

export default PaperTypesDialog;
